import type { PressableProps } from "../../primitives";
import { iconSize, iconSizeDesktop, type IconSizeKey } from "../../tokens";
import type { SpinnerProps } from "../spinner/types";
import type { TypographyProps } from "../typography";
import type { ButtonSize, ButtonVariant } from "./types";

type TypographyColor = NonNullable<TypographyProps["color"]>;

export const buttonTypographyVariant: Record<ButtonSize, "label-sm" | "label-md" | "label-lg"> = {
  sm: "label-sm",
  md: "label-md",
  lg: "label-lg",
};

export const buttonTypographyColor: Record<
  ButtonVariant,
  { default: TypographyColor; disabled: TypographyColor }
> = {
  primary: { default: "button-cta-on-cta", disabled: "button-cta-on-cta-disabled" },
  secondary: { default: "button-cta2-default", disabled: "button-cta2-on-cta2-disabled" },
  tertiary: { default: "button-cta2-default", disabled: "button-cta2-on-cta2-disabled" },
  neutral: { default: "neutral-foreground-default", disabled: "neutral-foreground-attenuated" },
  destructive: {
    default: "button-destructive-on-destructive",
    disabled: "button-destructive-on-destructive-disabled",
  },
  "destructive-secondary": {
    default: "button-destructive-default",
    disabled: "button-cta2-on-cta2-disabled",
  },
  success: {
    default: "system-success-on-success",
    disabled: "button-cta-on-cta-disabled",
  },
  "success-secondary": {
    default: "button-success-default",
    disabled: "button-disabled-cta-text-disabled",
  },
};

export const buttonTypographyStateClass: Partial<Record<ButtonVariant, string>> = {
  secondary: "group-hover:text-luce-button-cta2-hover group-active:text-luce-button-cta2-hover",
  tertiary: "group-hover:text-luce-button-cta2-hover group-active:text-luce-button-cta2-hover",
  "destructive-secondary":
    "group-hover:text-luce-button-destructive-default group-active:text-luce-button-destructive-default",
  "success-secondary":
    "group-hover:text-luce-button-success-hover group-active:text-luce-button-success-hover",
};
// neutral keeps `neutral-foreground-default` across states (only the surface changes), so no state class.

export const buttonSpinnerSize: Record<ButtonSize, SpinnerProps["size"]> = {
  sm: "small",
  md: "medium",
  lg: "large",
};

export const buttonSpinnerColor: Record<ButtonVariant, SpinnerProps["color"]> = {
  primary: "primary",
  secondary: "secondary",
  tertiary: "secondary",
  // Figma's Neutral loading cells draw the arc dark (CTA `8:2239`, Fab `3391:13525`)
  neutral: "neutral",
  destructive: "primary",
  "destructive-secondary": "destructive",
  success: "primary",
  "success-secondary": "success",
};

/**
 * A button's spinner colour: its look's, or the shared disabled grey when it's
 * also disabled, so the arc never sits white or blue on the disabled fill
 */
export const buttonSpinnerColorFor = (
  variant: ButtonVariant,
  disabled: boolean,
): SpinnerProps["color"] => (disabled ? "disabled" : buttonSpinnerColor[variant]);

/**
 * Native tap area: the `hitSlop` on each side that brings each size's mobile
 * height (24, 32, 40) to 44pt; web grows the same way on touch screens
 * (`minTouchTargetClassName`). Fabric counts hitSlop in the parent's overflow
 * inset, so a content-sized parent doesn't clip it. An `overflow-hidden` ancestor
 * still clips it, and a later sibling that overlaps it takes the touch first.
 */
export const buttonHitSlop: Record<ButtonSize, number> = {
  sm: 10,
  md: 6,
  lg: 2,
};

/** Mobile icon-size key per Button size. Button itself sizes icons with `buttonIconPx`. */
export const buttonIconSize: Record<ButtonSize, IconSizeKey> = {
  sm: "xs",
  md: "sm",
  lg: "lg",
};

// Figma's Button icons are 12/16/24, desktop 16/24/28: not one key step up
// from mobile (md is 16 / 24), so Button and FabButton pick a side in px
export const buttonIconPx: Record<ButtonSize, { mobile: number; desktop: number }> = {
  sm: { mobile: iconSize.xs, desktop: iconSizeDesktop.xs },
  md: { mobile: iconSize.sm, desktop: iconSizeDesktop.md },
  lg: { mobile: iconSize.lg, desktop: iconSizeDesktop.lg },
};

/** Icon color classes per variant and state (must match Typography color classes) */
export const buttonIconColor: Record<ButtonVariant, { default: string; disabled: string }> = {
  primary: {
    default: "text-luce-button-cta-onCta",
    disabled: "text-luce-button-cta-onCtaDisabled",
  },
  secondary: {
    default: "text-luce-button-cta2-default",
    disabled: "text-luce-button-cta2-onCta2Disabled",
  },
  tertiary: {
    default: "text-luce-button-cta2-default",
    disabled: "text-luce-button-cta2-onCta2Disabled",
  },
  neutral: {
    default: "text-luce-neutral-foreground-default",
    disabled: "text-luce-neutral-foreground-attenuated",
  },
  destructive: {
    default: "text-luce-button-destructive-onDestructive",
    disabled: "text-luce-button-destructive-onDestructiveDisabled",
  },
  "destructive-secondary": {
    default: "text-luce-button-destructive-default",
    disabled: "text-luce-button-cta2-onCta2Disabled",
  },
  success: {
    default: "text-luce-system-success-onSuccess",
    disabled: "text-luce-button-cta-onCtaDisabled",
  },
  "success-secondary": {
    default: "text-luce-button-success-default",
    disabled: "text-luce-button-disabled-ctaTextDisabled",
  },
};

/** Icon hover/active state classes (matches typography state classes) */
export const buttonIconStateClass: Partial<Record<ButtonVariant, string>> = {
  secondary: "group-hover:text-luce-button-cta2-hover group-active:text-luce-button-cta2-hover",
  tertiary: "group-hover:text-luce-button-cta2-hover group-active:text-luce-button-cta2-hover",
  "destructive-secondary":
    "group-hover:text-luce-button-destructive-default group-active:text-luce-button-destructive-default",
  "success-secondary":
    "group-hover:text-luce-button-success-hover group-active:text-luce-button-success-hover",
};

type ButtonAccessibilityContext = {
  label?: string;
  loading: boolean;
  isDisabled: boolean;
  asChild?: boolean;
} & Pick<PressableProps, "accessibilityLabel" | "accessibilityState">;

export function getButtonAccessibilityLabel(
  context: ButtonAccessibilityContext,
): string | undefined {
  // Priority: explicit accessibilityLabel > label prop. With asChild there's no
  // label, and the child names itself (its text or its own label) unless
  // accessibilityLabel is set, so no "Button" fallback replaces that name.
  const baseLabel = context.asChild
    ? context.accessibilityLabel
    : (context.accessibilityLabel ?? context.label ?? "Button");
  if (baseLabel === undefined) return undefined;
  // i18n: button.loadingSuffix
  return context.loading ? `${baseLabel} Loading` : baseLabel;
}

export function getButtonAccessibilityState(
  context: ButtonAccessibilityContext,
): PressableProps["accessibilityState"] {
  return {
    ...context.accessibilityState,
    disabled: context.isDisabled,
    busy: context.loading,
  };
}
